import React, { createContext, useContext } from 'react';
import type { ManageUnitsUnitView, ResponsePlanV2, UnitFiltersForm } from '@mark43/cad-api';
import type { AsyncResponsePlanStatus } from '../use-async-response-plan';

/**
 * The async response-plan surface the provider exposes to the store. The provider runs
 * `useAsyncResponsePlan` — the only async I/O — and mounts only when `asyncResponsePlansEnabled` is
 * true. Flag-off: no provider, consumers read `emptyAsyncContext` (neutral, no I/O), and the store
 * uses the legacy reducer. No PII/CJI flows through — only opaque ids, status, and the rendered plan
 * view.
 */
export type AsyncResponsePlanContextValue = {
    /** Ids the worker marked RECOMMENDED on the landed plan; feeds the derived selection. */
    recommendedUnitIds: Set<number>;
    /**
     * The landed plan's `initiallySelectedUnits`. Only the async path produces these; empty in the
     * neutral default.
     */
    landedRecommendedUnits: ManageUnitsUnitView[];
    /** Plan fields merged onto the units response: banners, primary unit, header. */
    responsePlanV2?: ResponsePlanV2;
    initialPrimaryUnitId?: number;
    initiallySelectedUnits: ManageUnitsUnitView[];
    /** Coarse plan status + first-load flag. `'idle'` / `true` in the neutral default (flag-off). */
    status: AsyncResponsePlanStatus;
    isFirstLoad: boolean;
    /** True only while a refresh is actively in flight; drives the drawer's refresh dim. */
    isRefreshing: boolean;
    /**
     * Kick entry points; all defer until the socket is CONNECTED. No-ops in the neutral default.
     */
    requestInitialKick: () => void;
    rekick: () => void;
    retry: () => void;
    /**
     * Refs the store writes each render and the kick reads at fire-time. Owned by the provider so the
     * kick (which runs above the store) reads the store's current selection/filters without the
     * provider re-rendering. Inert on flag-off — never written (the store uses the reducer).
     */
    selectedUnitIdsRef: React.MutableRefObject<number[]>;
    filtersRef: React.MutableRefObject<UnitFiltersForm>;
};

// Module-level constants so a flag-off store read never churns identity.
const EMPTY_RECOMMENDED_UNIT_IDS: Set<number> = new Set();
const EMPTY_LANDED_RECOMMENDED_UNITS: ManageUnitsUnitView[] = [];
const EMPTY_INITIALLY_SELECTED_UNITS: ManageUnitsUnitView[] = [];
const EMPTY_FILTERS: UnitFiltersForm = {
    dispatchAreaIds: [],
    emsUnitTypeIds: [],
    equipmentIds: [],
    fireUnitTypeIds: [],
    policeUnitTypeIds: [],
    skillIds: [],
};
const EMPTY_SELECTED_UNIT_IDS_REF: React.MutableRefObject<number[]> = { current: [] };
const EMPTY_FILTERS_REF: React.MutableRefObject<UnitFiltersForm> = { current: EMPTY_FILTERS };
const noop = (): void => {};

/**
 * Neutral no-I/O default used when no provider is mounted (`asyncResponsePlansEnabled` false, or any
 * consumer outside the provider). Refs are inert placeholders — flag-off the store uses the reducer
 * and never writes them, and no kick reads them.
 */
export const emptyAsyncContext: AsyncResponsePlanContextValue = {
    recommendedUnitIds: EMPTY_RECOMMENDED_UNIT_IDS,
    landedRecommendedUnits: EMPTY_LANDED_RECOMMENDED_UNITS,
    responsePlanV2: undefined,
    initialPrimaryUnitId: undefined,
    initiallySelectedUnits: EMPTY_INITIALLY_SELECTED_UNITS,
    status: 'idle',
    isFirstLoad: true,
    isRefreshing: false,
    requestInitialKick: noop,
    rekick: noop,
    retry: noop,
    selectedUnitIdsRef: EMPTY_SELECTED_UNIT_IDS_REF,
    filtersRef: EMPTY_FILTERS_REF,
};

export const AsyncResponsePlanContext =
    createContext<AsyncResponsePlanContextValue>(emptyAsyncContext);

export function useAsyncResponsePlanContext(): AsyncResponsePlanContextValue {
    return useContext(AsyncResponsePlanContext);
}
